import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Fragment, useCallback, useId, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { groupRowsBy } from '../../utils/gridGrouping';
import { compareGridCellStrings, nextSortState } from '../../utils/gridSort';

const DROP_ZONE_ID = 'group-drop-zone';

export interface GroupedColumnDef {
  id: string;
  /** Short label for group chips and group header rows */
  label: string;
  /** Table header cell content */
  header: React.ReactNode;
}

export interface GroupedDataGridProps<T> {
  columns: GroupedColumnDef[];
  rows: T[];
  getRowId: (row: T) => string;
  getGroupValue: (row: T, columnId: string) => string;
  renderRow: (row: T) => ReactElement;
  colSpan: number;
  emptyMessage: string;
  /** Explains that grouping applies to current page / client-side */
  groupHint?: string;
  /**
   * Per-column text filters (substring match, case-insensitive) on the current `rows`.
   * Uses the same strings as grouping via `getGroupValue`. Default true.
   */
  enableColumnFilters?: boolean;
  /** Client-side sort by column (uses same values as grouping). Default true. */
  enableSorting?: boolean;
  /**
   * When set, sort column/direction and header clicks are owned by the parent (e.g. server-backed sort).
   */
  controlledSortColumn?: string | null;
  controlledSortDirection?: 'asc' | 'desc' | null;
  onControlledSortClick?: (columnId: string) => void;
  /**
   * When true, rows are shown in API order; no client-side reorder (use with server sort for the active column).
   */
  trustServerRowOrder?: boolean;
}

function ColumnHeaderCell({
  columnId,
  children,
  sortColumnId,
  sortDirection,
  onSortClick,
  enableSorting
}: {
  columnId: string;
  children: React.ReactNode;
  sortColumnId: string | null;
  sortDirection: 'asc' | 'desc' | null;
  onSortClick: (columnId: string) => void;
  enableSorting: boolean;
}): ReactElement {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `drag-col-${columnId}`,
    data: { type: 'column-header', columnId }
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  if (!enableSorting) {
    return (
      <th
        ref={setNodeRef}
        style={style}
        className={`grid-th-draggable${isDragging ? ' grid-th-draggable--dragging' : ''}`}
        {...listeners}
        {...attributes}
      >
        {children}
      </th>
    );
  }

  const isSorted = sortColumnId === columnId;
  const ariaSort: 'none' | 'ascending' | 'descending' =
    !isSorted || !sortDirection ? 'none' : sortDirection === 'asc' ? 'ascending' : 'descending';

  return (
    <th className="grid-th-header-cell" aria-sort={ariaSort}>
      <div className="grid-th-inner">
        <button
          type="button"
          className={`grid-th-sort${isSorted ? ' grid-th-sort--active' : ''}`}
          onClick={() => onSortClick(columnId)}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span className="grid-th-sort-label">{children}</span>
          <span className="grid-sort-indicator" aria-hidden>
            {isSorted ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
          </span>
        </button>
        <button
          type="button"
          className={`grid-th-drag-handle${isDragging ? ' grid-th-drag-handle--dragging' : ''}`}
          ref={setNodeRef}
          style={style}
          {...listeners}
          {...attributes}
          aria-label="Drag to group by this column"
          title="Drag to group"
        >
          ⠿
        </button>
      </div>
    </th>
  );
}

function SortableGroupChip({
  id,
  label,
  onRemove
}: {
  id: string;
  label: string;
  onRemove: () => void;
}): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : undefined,
    zIndex: isDragging ? 2 : undefined
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group-chip${isDragging ? ' group-chip--dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <span className="group-chip-label">{label}</span>
      <button
        type="button"
        className="group-chip-remove"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        aria-label={`Remove grouping by ${label}`}
      >
        ×
      </button>
    </div>
  );
}

function GroupDropStrip({
  groupColumnIds,
  columnLabel,
  onRemoveColumn
}: {
  groupColumnIds: string[];
  columnLabel: (columnId: string) => string;
  onRemoveColumn: (columnId: string) => void;
}): ReactElement {
  const { setNodeRef, isOver } = useDroppable({ id: DROP_ZONE_ID });

  return (
    <div
      ref={setNodeRef}
      className={`group-drop-strip${isOver ? ' group-drop-strip--over' : ''}`}
      aria-label="Group by drop zone"
    >
      {groupColumnIds.length === 0 && <span className="group-drop-placeholder">Drag column headers here to group</span>}
      <SortableContext items={groupColumnIds} strategy={horizontalListSortingStrategy}>
        {groupColumnIds.map((colId) => (
          <SortableGroupChip
            key={colId}
            id={colId}
            label={columnLabel(colId)}
            onRemove={() => onRemoveColumn(colId)}
          />
        ))}
      </SortableContext>
    </div>
  );
}

function renderGroupedRows<T>(
  rows: T[],
  remainingGroupIds: string[],
  getGroupValue: (row: T, columnId: string) => string,
  getRowId: (row: T) => string,
  renderRow: (row: T) => ReactElement,
  colSpan: number,
  columnLabel: (id: string) => string,
  depth: number
): ReactNode[] {
  if (rows.length === 0) {
    return [];
  }
  if (remainingGroupIds.length === 0) {
    return rows.map((row) => <Fragment key={getRowId(row)}>{renderRow(row)}</Fragment>);
  }

  const colId = remainingGroupIds[0];
  const groups = groupRowsBy(rows, (r) => getGroupValue(r, colId));
  const rest = remainingGroupIds.slice(1);
  const out: ReactNode[] = [];

  groups.forEach((g, idx) => {
    out.push(
      <tr key={`grp-${depth}-${colId}-${g.key}-${idx}`} className="grid-group-row">
        <td colSpan={colSpan} className="grid-group-label" style={{ paddingLeft: `${10 + depth * 14}px` }}>
          <span className="grid-group-title">{columnLabel(colId)}</span>
          <span className="grid-group-value">{g.key}</span>
          <span className="grid-group-count">{g.rows.length} rows</span>
        </td>
      </tr>
    );
    out.push(...renderGroupedRows(g.rows, rest, getGroupValue, getRowId, renderRow, colSpan, columnLabel, depth + 1));
  });

  return out;
}

export function GroupedDataGrid<T>({
  columns,
  rows,
  getRowId,
  getGroupValue,
  renderRow,
  colSpan,
  emptyMessage,
  groupHint,
  enableColumnFilters = true,
  enableSorting = true,
  controlledSortColumn,
  controlledSortDirection,
  onControlledSortClick,
  trustServerRowOrder = false
}: GroupedDataGridProps<T>): ReactElement {
  const [groupColumnIds, setGroupColumnIds] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [internalSortState, setInternalSortState] = useState<{
    column: string | null;
    direction: 'asc' | 'desc' | null;
  }>({ column: null, direction: null });
  const stripHeadingId = useId();

  const sortControlled = onControlledSortClick != null;
  const sortColumn = sortControlled ? (controlledSortColumn ?? null) : internalSortState.column;
  const sortDirection = sortControlled ? (controlledSortDirection ?? null) : internalSortState.direction;

  const handleSortClick = useCallback(
    (columnId: string) => {
      if (sortControlled) {
        onControlledSortClick(columnId);
        return;
      }
      setInternalSortState((prev) => {
        const next = nextSortState(prev.column, prev.direction, columnId);
        return { column: next.column, direction: next.direction };
      });
    },
    [sortControlled, onControlledSortClick]
  );

  const columnLabel = useMemo(() => {
    const map = new Map(columns.map((c) => [c.id, c.label]));
    return (id: string): string => map.get(id) ?? id;
  }, [columns]);

  const filteredRows = useMemo(() => {
    if (!enableColumnFilters) {
      return rows;
    }
    const active = Object.entries(columnFilters).filter(([, v]) => v.trim().length > 0);
    if (active.length === 0) {
      return rows;
    }
    return rows.filter((row) =>
      active.every(([colId, raw]) => {
        const term = raw.trim().toLowerCase();
        const cell = getGroupValue(row, colId).toLowerCase();
        return cell.includes(term);
      })
    );
  }, [rows, columnFilters, getGroupValue, enableColumnFilters]);

  const sortedRows = useMemo(() => {
    if (!enableSorting || !sortColumn || !sortDirection) {
      return filteredRows;
    }
    if (trustServerRowOrder) {
      return filteredRows;
    }
    const col = sortColumn;
    const mult = sortDirection === 'asc' ? 1 : -1;
    return [...filteredRows].sort(
      (a, b) => compareGridCellStrings(getGroupValue(a, col), getGroupValue(b, col)) * mult
    );
  }, [filteredRows, sortColumn, sortDirection, getGroupValue, enableSorting, trustServerRowOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over) {
      return;
    }

    const activeId = String(active.id);

    if (activeId.startsWith('drag-col-')) {
      const colId = activeId.slice('drag-col-'.length);
      if (over.id === DROP_ZONE_ID && !groupColumnIds.includes(colId)) {
        setGroupColumnIds((prev) => [...prev, colId]);
      }
      return;
    }

    if (groupColumnIds.length > 0 && groupColumnIds.includes(activeId) && groupColumnIds.includes(String(over.id))) {
      setGroupColumnIds((items) => {
        const oldIndex = items.indexOf(activeId);
        const newIndex = items.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const noRowsFromServer = rows.length === 0;
  const noRowsAfterFilter = !noRowsFromServer && filteredRows.length === 0;

  const bodyContent =
    noRowsFromServer ? (
      <tr>
        <td colSpan={colSpan} className="empty-cell">
          {emptyMessage}
        </td>
      </tr>
    ) : noRowsAfterFilter ? (
      <tr>
        <td colSpan={colSpan} className="empty-cell">
          No rows match your column filters.
        </td>
      </tr>
    ) : groupColumnIds.length === 0 ? (
      sortedRows.map((row) => <Fragment key={getRowId(row)}>{renderRow(row)}</Fragment>)
    ) : (
      renderGroupedRows(
        sortedRows,
        groupColumnIds,
        getGroupValue,
        getRowId,
        renderRow,
        colSpan,
        columnLabel,
        0
      )
    );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className="grouped-grid-chrome" role="region" aria-labelledby={stripHeadingId}>
        <p id={stripHeadingId} className="sr-only">
          Group by: drag the grip beside each column title into the strip below, reorder chips to nest groups, or remove
          with ×.
          {enableSorting ? ' Click a column title to sort (ascending, descending, then clear).' : ''}
          {enableColumnFilters ? ' Use the filter row under the headers to narrow rows by typed text.' : ''}
        </p>
        {groupHint && <p className="hint grouped-grid-hint">{groupHint}</p>}
        <GroupDropStrip
          groupColumnIds={groupColumnIds}
          columnLabel={columnLabel}
          onRemoveColumn={(id) => setGroupColumnIds((prev) => prev.filter((x) => x !== id))}
        />
        <div className="requests-grid-wrap">
          <table className="requests-grid">
            <thead>
              <tr>
                {columns.map((col) => (
                  <ColumnHeaderCell
                    key={col.id}
                    columnId={col.id}
                    sortColumnId={sortColumn}
                    sortDirection={sortDirection}
                    onSortClick={handleSortClick}
                    enableSorting={enableSorting}
                  >
                    {col.header}
                  </ColumnHeaderCell>
                ))}
              </tr>
              {enableColumnFilters && (
                <tr className="grid-filter-row">
                  {columns.map((col) => {
                    const filterFieldId = `${stripHeadingId}-filter-${col.id}`;
                    return (
                    <th key={`f-${col.id}`} className="grid-filter-cell">
                      <label className="sr-only" htmlFor={filterFieldId}>
                        Filter {col.label}
                      </label>
                      <input
                        id={filterFieldId}
                        type="search"
                        className="grid-col-filter"
                        placeholder="Filter…"
                        value={columnFilters[col.id] ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setColumnFilters((prev) => {
                            if (v === '') {
                              const next = { ...prev };
                              delete next[col.id];
                              return next;
                            }
                            return { ...prev, [col.id]: v };
                          });
                        }}
                        autoComplete="off"
                      />
                    </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            <tbody>{bodyContent}</tbody>
          </table>
        </div>
      </div>
    </DndContext>
  );
}
